import type { AlertaHistorico } from "../../lib/alertas/tipos";
import { CANAIS, SEVERIDADES, STATUSES } from "../../lib/alertas/tipos";
import { formatarDataHoraISO, formatarDataISO } from "../../lib/formato/data";
import { formatarDecimal } from "../../lib/formato/decimal";

/*
 * Tabela do historico de alertas (FE-7 — Tela 7 §10.5).
 *
 * Espelha `TabelaTrilha` (`<table>` real, tokens CPMH). Renderiza o array PURO de
 * `AlertaHistorico` (sem total/envelope). Colunas: data de referencia, empresa
 * (resolvida por `resolverEmpresa`; `empresa_id === null` => "Grupo"), severidade,
 * saldo observado (string-based, alinhado a direita, vermelho se negativo), canal,
 * status (botao clicavel SO em `dead_letter`, que abre o modal de erro) e disparo.
 *
 * Acoes: "Reagendar" SO aparece quando o status e `dead_letter` E o usuario tem
 * permissao (`podeReagendar`) — caso contrario "—".
 *
 * ESTADO VAZIO: a query resolveu com `[]` (nenhum alerta para os filtros) =>
 * mensagem clara, nao uma tabela vazia.
 */

const SEM_VALOR = "—";

/** Resolve o par valor->rotulo de uma tupla `as const`; valor cru se ausente. */
function rotuloDe(
  tabela: ReadonlyArray<readonly [string, string]>,
  valor: string,
): string {
  const par = tabela.find(([chave]) => chave === valor);
  return par ? par[1] : valor;
}

interface TabelaAlertasProps {
  itens: AlertaHistorico[];
  /** Resolve `empresa_id` (string) em um rotulo legivel. `null` => "Grupo". */
  resolverEmpresa: (empresaId: string) => string;
  /** O usuario pode reagendar (acao `reagendar_notificacao` no escopo)? */
  podeReagendar: boolean;
  /** Abre o modal de erro de um alerta em dead_letter. */
  aoAbrirErro: (item: AlertaHistorico) => void;
  /** Abre o modal de reagendamento de um alerta em dead_letter. */
  aoReagendar: (item: AlertaHistorico) => void;
}

export function TabelaAlertas({
  itens,
  resolverEmpresa,
  podeReagendar,
  aoAbrirErro,
  aoReagendar,
}: TabelaAlertasProps): JSX.Element {
  if (itens.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">
        Nenhum alerta encontrado para estes filtros.
      </p>
    );
  }

  return (
    <table className="w-full border-collapse font-corpo text-base text-off-black">
      <thead>
        <tr className="border-b border-platinum text-left">
          <th className="px-2 py-1 font-subtitulo">Data referência</th>
          <th className="px-2 py-1 font-subtitulo">Empresa</th>
          <th className="px-2 py-1 font-subtitulo">Severidade</th>
          <th className="px-2 py-1 font-subtitulo">Saldo observado</th>
          <th className="px-2 py-1 font-subtitulo">Canal</th>
          <th className="px-2 py-1 font-subtitulo">Status</th>
          <th className="px-2 py-1 font-subtitulo">Disparado em</th>
          <th className="px-2 py-1 font-subtitulo">Ações</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((item) => {
          const deadLetter = item.status === "dead_letter";
          const saldoNegativo = item.saldo_observado.startsWith("-");
          return (
            <tr
              key={item.id_outbox}
              className="border-b border-platinum bg-snow"
            >
              <td className="px-2 py-1">
                {formatarDataISO(item.data_referencia)}
              </td>
              <td className="px-2 py-1">
                {item.empresa_id === null
                  ? "Grupo"
                  : resolverEmpresa(item.empresa_id)}
              </td>
              <td className="px-2 py-1">
                {rotuloDe(SEVERIDADES, item.severidade)}
              </td>
              <td
                className={
                  saldoNegativo
                    ? "px-2 py-1 text-right tabular-nums text-erro"
                    : "px-2 py-1 text-right tabular-nums"
                }
              >
                {formatarDecimal(item.saldo_observado)}
              </td>
              <td className="px-2 py-1">{rotuloDe(CANAIS, item.canal)}</td>
              <td className="px-2 py-1">
                {deadLetter ? (
                  <button
                    type="button"
                    onClick={() => aoAbrirErro(item)}
                    className="text-erro hover:text-flame"
                  >
                    {rotuloDe(STATUSES, item.status)}
                  </button>
                ) : (
                  rotuloDe(STATUSES, item.status)
                )}
              </td>
              <td className="px-2 py-1">
                {formatarDataHoraISO(item.disparado_em)}
              </td>
              <td className="px-2 py-1">
                {deadLetter && podeReagendar ? (
                  <button
                    type="button"
                    onClick={() => aoReagendar(item)}
                    className="text-flame hover:text-off-black"
                  >
                    Reagendar
                  </button>
                ) : (
                  SEM_VALOR
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
